import { Card } from "@pericles/framework-frontend";
import { getLongwallsDesignTheme } from "@pericles/longwalls-design-system";
import { type CSSProperties, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  fetchLongwallsAtlasGeneralizationPack,
  fetchLongwallsCompositionCatalog,
  fetchLongwallsCustomerRuntimeBridgePack,
  fetchLongwallsEnterpriseProofBoard,
  fetchLongwallsFoundryGeneralizationPack,
  fetchLongwallsConciergerieAssemblyPack,
  fetchLongwallsJarvisDefaultUsagePack,
  fetchLongwallsJarvisDepthPack,
  fetchLongwallsLegacyReverseEngineeringPack,
  fetchLongwallsLiveAccountConversionPack,
  fetchLongwallsLivePilotProofPack,
  fetchLongwallsLighthouseMotionPack,
  fetchLongwallsProductionRuntimeVerificationPack,
  type LongwallsAtlasGeneralizationPack,
  type LongwallsCompositionCatalog,
  type LongwallsCustomerRuntimeBridgePack,
  type LongwallsEnterpriseProofBoard,
  type LongwallsFoundryGeneralizationPack,
  type LongwallsConciergerieAssemblyPack,
  type LongwallsJarvisDefaultUsagePack,
  type LongwallsJarvisDepthPack,
  type LongwallsLegacyReverseEngineeringPack,
  type LongwallsLiveAccountConversionPack,
  type LongwallsLivePilotProofPack,
  type LongwallsLighthouseMotionPack,
  type LongwallsProductionRuntimeVerificationPack,
} from "../api/longwallsEnterpriseClient";
import { Button } from "./ui";

const PRIMARY_MODULES = [
  {
    id: "atlas",
    title: "Atlas",
    badge: "Discovery",
    description:
      "Map enterprise processes, isolate transformation use cases, and build a board-ready roadmap with explicit prioritization.",
    value: "Portfolio truth",
    href: "/atlas",
    accent: "violet",
  },
  {
    id: "foundry",
    title: "Foundry",
    badge: "Blueprint",
    description:
      "Compile discovery into deterministic briefs, specs, environment contracts, and handoff-ready execution packages.",
    value: "Deterministic blueprint",
    href: "/foundry",
    accent: "amber",
  },
  {
    id: "jarvis",
    title: "Jarvis",
    badge: "Delivery",
    description:
      "Run governed autonomous delivery with approvals, replay, QA, readiness, and production-safe operating control.",
    value: "Governed execution",
    href: "/jarvis",
    accent: "emerald",
  },
  {
    id: "governance",
    title: "Governance",
    badge: "Control",
    description:
      "Keep entitlements, rollout posture, and enterprise operating defaults aligned before widening execution.",
    value: "Control posture",
    href: "/governance",
    accent: "slate",
  },
] as const;

const OPERATING_LINE = [
  {
    id: "stage-1",
    title: "1. Diagnose and prioritize",
    description: "Atlas converts fragmented discovery into a use-case portfolio, sequencing logic, and sponsor-ready roadmap.",
  },
  {
    id: "stage-2",
    title: "2. Lock the blueprint",
    description: "Foundry turns that portfolio into deterministic contracts, architecture choices, and acceptance-ready specs.",
  },
  {
    id: "stage-3",
    title: "3. Deliver with proof",
    description: "Jarvis executes the path with policy, approvals, QA, replay, and evidence instead of prompt-driven improvisation.",
  },
] as const;

const SECONDARY_SURFACES = [
  { id: "agora", title: "Agora", description: "Account and customer context.", href: "/accounts" },
  { id: "forum", title: "Forum", description: "Meetings, transcripts, and operating context.", href: "/forum" },
] as const;

export function HomeDashboard(): JSX.Element {
  const navigate = useNavigate();
  const [proofBoard, setProofBoard] = useState<LongwallsEnterpriseProofBoard | null>(null);
  const [proofError, setProofError] = useState<string | null>(null);
  const [compositionCatalog, setCompositionCatalog] = useState<LongwallsCompositionCatalog | null>(null);
  const [compositionError, setCompositionError] = useState<string | null>(null);
  const [lighthouseMotion, setLighthouseMotion] = useState<LongwallsLighthouseMotionPack | null>(null);
  const [lighthouseMotionError, setLighthouseMotionError] = useState<string | null>(null);
  const [liveAccountConversion, setLiveAccountConversion] = useState<LongwallsLiveAccountConversionPack | null>(null);
  const [liveAccountConversionError, setLiveAccountConversionError] = useState<string | null>(null);
  const [livePilotProof, setLivePilotProof] = useState<LongwallsLivePilotProofPack | null>(null);
  const [livePilotProofError, setLivePilotProofError] = useState<string | null>(null);
  const [customerRuntimeBridge, setCustomerRuntimeBridge] = useState<LongwallsCustomerRuntimeBridgePack | null>(null);
  const [customerRuntimeBridgeError, setCustomerRuntimeBridgeError] = useState<string | null>(null);
  const [productionRuntimeVerification, setProductionRuntimeVerification] =
    useState<LongwallsProductionRuntimeVerificationPack | null>(null);
  const [productionRuntimeVerificationError, setProductionRuntimeVerificationError] = useState<string | null>(null);
  const [legacyReverseEngineering, setLegacyReverseEngineering] = useState<LongwallsLegacyReverseEngineeringPack | null>(null);
  const [legacyReverseEngineeringError, setLegacyReverseEngineeringError] = useState<string | null>(null);
  const [atlasGeneralization, setAtlasGeneralization] = useState<LongwallsAtlasGeneralizationPack | null>(null);
  const [atlasGeneralizationError, setAtlasGeneralizationError] = useState<string | null>(null);
  const [foundryGeneralization, setFoundryGeneralization] = useState<LongwallsFoundryGeneralizationPack | null>(null);
  const [foundryGeneralizationError, setFoundryGeneralizationError] = useState<string | null>(null);
  const [jarvisDepth, setJarvisDepth] = useState<LongwallsJarvisDepthPack | null>(null);
  const [jarvisDepthError, setJarvisDepthError] = useState<string | null>(null);
  const [conciergerieAssembly, setConciergerieAssembly] = useState<LongwallsConciergerieAssemblyPack | null>(null);
  const [conciergerieAssemblyError, setConciergerieAssemblyError] = useState<string | null>(null);
  const [jarvisDefaultUsage, setJarvisDefaultUsage] = useState<LongwallsJarvisDefaultUsagePack | null>(null);
  const [jarvisDefaultUsageError, setJarvisDefaultUsageError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsEnterpriseProofBoard()
      .then((payload) => {
        if (cancelled) return;
        setProofBoard(payload);
        setProofError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setProofBoard(null);
        setProofError(error instanceof Error ? error.message : "Failed to load enterprise proof board.");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsFoundryGeneralizationPack()
      .then((payload) => {
        if (cancelled) return;
        setFoundryGeneralization(payload);
        setFoundryGeneralizationError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setFoundryGeneralization(null);
        setFoundryGeneralizationError(
          error instanceof Error ? error.message : "Failed to load the LongWalls Foundry generalization pack.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsJarvisDepthPack()
      .then((payload) => {
        if (cancelled) return;
        setJarvisDepth(payload);
        setJarvisDepthError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setJarvisDepth(null);
        setJarvisDepthError(error instanceof Error ? error.message : "Failed to load the LongWalls Jarvis depth pack.");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsConciergerieAssemblyPack()
      .then((payload) => {
        if (cancelled) return;
        setConciergerieAssembly(payload);
        setConciergerieAssemblyError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setConciergerieAssembly(null);
        setConciergerieAssemblyError(
          error instanceof Error ? error.message : "Failed to load the LongWalls Conciergerie assembly pack.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsJarvisDefaultUsagePack()
      .then((payload) => {
        if (cancelled) return;
        setJarvisDefaultUsage(payload);
        setJarvisDefaultUsageError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setJarvisDefaultUsage(null);
        setJarvisDefaultUsageError(
          error instanceof Error ? error.message : "Failed to load the LongWalls Jarvis default usage pack.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsLiveAccountConversionPack()
      .then((payload) => {
        if (cancelled) return;
        setLiveAccountConversion(payload);
        setLiveAccountConversionError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setLiveAccountConversion(null);
        setLiveAccountConversionError(
          error instanceof Error ? error.message : "Failed to load the LongWalls live-account conversion pack.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsLivePilotProofPack()
      .then((payload) => {
        if (cancelled) return;
        setLivePilotProof(payload);
        setLivePilotProofError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setLivePilotProof(null);
        setLivePilotProofError(error instanceof Error ? error.message : "Failed to load the LongWalls live pilot proof pack.");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsCustomerRuntimeBridgePack()
      .then((payload) => {
        if (cancelled) return;
        setCustomerRuntimeBridge(payload);
        setCustomerRuntimeBridgeError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setCustomerRuntimeBridge(null);
        setCustomerRuntimeBridgeError(
          error instanceof Error ? error.message : "Failed to load the LongWalls customer runtime bridge pack.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsProductionRuntimeVerificationPack()
      .then((payload) => {
        if (cancelled) return;
        setProductionRuntimeVerification(payload);
        setProductionRuntimeVerificationError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setProductionRuntimeVerification(null);
        setProductionRuntimeVerificationError(
          error instanceof Error
            ? error.message
            : "Failed to load the LongWalls production runtime verification pack.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsLegacyReverseEngineeringPack()
      .then((payload) => {
        if (cancelled) return;
        setLegacyReverseEngineering(payload);
        setLegacyReverseEngineeringError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setLegacyReverseEngineering(null);
        setLegacyReverseEngineeringError(
          error instanceof Error ? error.message : "Failed to load the LongWalls legacy reverse-engineering pack.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsLighthouseMotionPack()
      .then((payload) => {
        if (cancelled) return;
        setLighthouseMotion(payload);
        setLighthouseMotionError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setLighthouseMotion(null);
        setLighthouseMotionError(
          error instanceof Error ? error.message : "Failed to load the LongWalls lighthouse pilot motion pack.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsCompositionCatalog()
      .then((payload) => {
        if (cancelled) return;
        setCompositionCatalog(payload);
        setCompositionError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setCompositionCatalog(null);
        setCompositionError(error instanceof Error ? error.message : "Failed to load composition catalog.");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    void fetchLongwallsAtlasGeneralizationPack()
      .then((payload) => {
        if (cancelled) return;
        setAtlasGeneralization(payload);
        setAtlasGeneralizationError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        setAtlasGeneralization(null);
        setAtlasGeneralizationError(
          error instanceof Error ? error.message : "Failed to load the LongWalls Atlas generalization pack.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="home-dashboard longwalls-shell mx-auto flex w-full max-w-[1440px] flex-col gap-6">
      <section className="home-dashboard__hero longwalls-shell__hero" data-testid="longwalls-shell-hero">
        <div className="home-dashboard__hero-copy">
          <span className="home-dashboard__eyebrow">Enterprise transformation platform</span>
          <div className="space-y-3">
            <h1 className="home-dashboard__title">LongWalls</h1>
            <p className="home-dashboard__subtitle">
              Compress the path from process diagnosis to deterministic blueprint to governed autonomous delivery,
              without falling into low-code improvisation or opaque AI generation.
            </p>
          </div>
        </div>
        <div className="home-dashboard__hero-meta" aria-label="LongWalls status summary">
          <div className="home-dashboard__meta-card">
            <span className="home-dashboard__meta-label">Core modules</span>
            <strong className="home-dashboard__meta-value">Atlas • Foundry • Jarvis</strong>
          </div>
          <div className="home-dashboard__meta-card">
            <span className="home-dashboard__meta-label">Target motion</span>
            <strong className="home-dashboard__meta-value">Decision-to-delivery compression</strong>
          </div>
          <div className="home-dashboard__meta-card">
            <span className="home-dashboard__meta-label">Operating stance</span>
            <strong className="home-dashboard__meta-value">Deterministic, evidence-first, governed</strong>
          </div>
          <div className="home-dashboard__meta-card">
            <span className="home-dashboard__meta-label">Target buyers</span>
            <strong className="home-dashboard__meta-value">Large enterprises first</strong>
          </div>
        </div>
      </section>

      <section aria-label="Composition engine" data-testid="longwalls-composition-catalog">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Composition engine</div>
              <div className="text-lg font-semibold text-foreground">Contracts, bricks, and archetypes</div>
              <p className="text-sm text-muted-foreground">
                LongWalls will not stop at a single runnable template. Composition becomes the next control layer:
                brick selection, archetype choice, and custom delta are now explicit objects.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => navigate("/foundry")}>
                Open Foundry
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate("/foundry?projectId=CONCIERGE-CORE&legacyRepoId=conciergerie-2-0")}
              >
                Import Conciergerie into Foundry
              </Button>
            </div>
          </div>

          {compositionCatalog ? (
            <>
              <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Archetypes</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{compositionCatalog.summary.archetypeCount}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Bricks</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{compositionCatalog.summary.brickCount}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Validated docs</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{compositionCatalog.summary.validatedDocuments}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Harvested sources</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{compositionCatalog.summary.sourceRepoCount}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Candidate bricks</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{compositionCatalog.summary.candidateBrickCount}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Priority packs</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{compositionCatalog.summary.priorityPackCount}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Extracted bricks</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {compositionCatalog.summary.canonicalImplementationCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Runtime governance bricks</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {compositionCatalog.summary.canonicalGovernanceRuntimeCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Jarvis-affine bricks</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {compositionCatalog.summary.canonicalJarvisAffineCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Reuse contexts</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {compositionCatalog.summary.canonicalReuseContextCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Differentiated profiles</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {compositionCatalog.summary.differentiatedProfileCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Design variants</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {compositionCatalog.summary.designVariantCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Differentiated brick coverage</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {compositionCatalog.summary.uniqueDifferentiatedBrickCount}
                  </div>
                </div>
              </div>
              <div className="grid gap-3 xl:grid-cols-[1.15fr,0.85fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Canonical brick contract</div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        Phases 1 through 8 now freeze the reusable service layer behind explicit manifests, security,
                        quality, compatibility, extraction, and release posture.
                      </div>
                    </div>
                    <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {compositionCatalog.canonical.releaseStatus.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Canonical manifests</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {compositionCatalog.summary.canonicalManifestCount}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Security profiles</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {compositionCatalog.summary.canonicalSecurityProfileCount}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Quality profiles</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {compositionCatalog.summary.canonicalQualityProfileCount}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Compatibility entries</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {compositionCatalog.summary.canonicalCompatibilityEntryCount}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Assembly bricks</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {compositionCatalog.summary.canonicalAssemblyBrickCount}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Shared bricks</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {compositionCatalog.summary.canonicalSharedBrickCount}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Canonical brick selection</div>
                      <div className="mt-3 space-y-3">
                        {compositionCatalog.canonical.manifests.slice(0, 5).map((brick) => (
                          <div key={brick.id} className="rounded-md border border-border bg-background p-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="text-sm font-semibold text-foreground">{brick.title}</div>
                              <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {brick.rolloutReadiness.replace(/_/g, " ")}
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                              <span className="rounded-full border border-border px-2 py-1">{brick.owner}</span>
                              <span className="rounded-full border border-border px-2 py-1">{brick.maturity}</span>
                              <span className="rounded-full border border-border px-2 py-1">{brick.reusePotential} reuse</span>
                              <span className="rounded-full border border-border px-2 py-1">{brick.consumptionMode}</span>
                              <span className="rounded-full border border-border px-2 py-1">{brick.controlPlaneAffinity}</span>
                            </div>
                            <div className="mt-2 text-xs text-muted-foreground">{brick.targetPackage}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Security posture</div>
                        <div className="mt-3 space-y-2">
                          {compositionCatalog.canonical.securityHighlights.slice(0, 3).map((profile) => (
                            <div key={profile.brickId} className="rounded-md border border-border bg-background p-3">
                              <div className="text-sm font-semibold text-foreground">{profile.brickId}</div>
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                                <span className="rounded-full border border-border px-2 py-1">{profile.classification}</span>
                                <span className="rounded-full border border-border px-2 py-1">
                                  {profile.approvalRequired ? "approval required" : "approval optional"}
                                </span>
                                <span className="rounded-full border border-border px-2 py-1">{profile.secretHandling}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Quality and compatibility</div>
                        <div className="mt-3 grid gap-3">
                          {compositionCatalog.canonical.qualityHighlights.slice(0, 2).map((profile) => (
                            <div key={profile.brickId} className="rounded-md border border-border bg-background p-3">
                              <div className="text-sm font-semibold text-foreground">{profile.brickId}</div>
                              <div className="mt-2 text-xs text-muted-foreground">
                                {profile.qaTiers.join(" / ")} / {profile.playwrightCoverage} / {profile.stabilityTarget}
                              </div>
                            </div>
                          ))}
                          {compositionCatalog.canonical.compatibilityHighlights.slice(0, 2).map((entry) => (
                            <div key={entry.brickId} className="rounded-md border border-border bg-background p-3">
                              <div className="text-sm font-semibold text-foreground">{entry.brickId}</div>
                              <div className="mt-2 text-xs text-muted-foreground">
                                {entry.archetypeCount} archetypes / {entry.targetPackage}
                                {entry.blockedByCount > 0 ? ` / ${entry.blockedByCount} blockers` : " / no blockers"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Runtime control pack</div>
                        <div className="mt-3 grid gap-3">
                          {compositionCatalog.canonical.governanceHighlights.slice(0, 4).map((brick) => (
                            <div key={brick.brickId} className="rounded-md border border-border bg-background p-3">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="text-sm font-semibold text-foreground">{brick.title}</div>
                                <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                  {brick.consumptionMode}
                                </span>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                                <span className="rounded-full border border-border px-2 py-1">{brick.controlPlaneAffinity}</span>
                                <span className="rounded-full border border-border px-2 py-1">
                                  {brick.consumableInFoundry ? "foundry" : "foundry off"}
                                </span>
                                <span className="rounded-full border border-border px-2 py-1">
                                  {brick.consumableInJarvis ? "jarvis" : "jarvis off"}
                                </span>
                              </div>
                              <div className="mt-2 text-xs text-muted-foreground">
                                {brick.runtimeSourcePackage ?? "runtime source unavailable"}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Brick Catalog v1 release</div>
                    <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {compositionCatalog.canonical.extractionStatus.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="mt-3 space-y-3 text-sm text-muted-foreground">
                    <p>
                      The release line is no longer frozen at governance extraction. The catalog now spans governance,
                      revenue CRM, acquisition, premium UI, agentic templates, and the final domain-heavy packs that
                      complete Brick Catalog v1.
                    </p>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-sm font-semibold text-foreground">Release evidence</div>
                      <div className="mt-3 grid gap-3">
                        {compositionCatalog.canonical.implementationHighlights.slice(0, 4).map((record) => (
                          <div key={record.brickId} className="rounded-md border border-border bg-background p-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="text-sm font-semibold text-foreground">{record.brickId}</div>
                              <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {record.extractionStatus}
                              </span>
                            </div>
                            <div className="mt-2 text-xs text-muted-foreground">{record.exportPath}</div>
                            <div className="mt-2 text-xs text-muted-foreground">
                              {record.runtimeSourcePackage ?? "No runtime source package"}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                              <span className="rounded-full border border-border px-2 py-1">
                                {record.reuseContextCount} reuse contexts
                              </span>
                              <span className="rounded-full border border-border px-2 py-1">
                                {record.consumableInFoundry ? "foundry" : "foundry off"}
                              </span>
                              <span className="rounded-full border border-border px-2 py-1">
                                {record.consumableInJarvis ? "jarvis" : "jarvis off"}
                              </span>
                              <span className="rounded-full border border-border px-2 py-1">{record.qaStatus} QA</span>
                              <span className="rounded-full border border-border px-2 py-1">
                                {record.securityStatus} security
                              </span>
                              <span className="rounded-full border border-border px-2 py-1">{record.uiStatus} UI</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-sm font-semibold text-foreground">Released pack sequence</div>
                      <div className="mt-3 grid gap-3">
                        {compositionCatalog.harvest.priorityPacks.map((pack) => (
                          <div key={pack.id} className="rounded-md border border-border bg-background p-3">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="text-sm font-semibold text-foreground">{pack.title}</div>
                              <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {pack.targetPhase}
                              </span>
                            </div>
                            <div className="mt-2 text-xs text-muted-foreground">{pack.summary}</div>
                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                              <span className="rounded-full border border-border px-2 py-1">{pack.status}</span>
                              <span className="rounded-full border border-border px-2 py-1">{pack.candidateCount} candidates</span>
                              <span className="rounded-full border border-border px-2 py-1">{pack.sourceRepoIds.length} source repos</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                <div className="rounded-md border border-border bg-background p-4 lg:col-span-2">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Differentiated assembly profiles
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {compositionCatalog.differentiation.comparisonHeadline}
                      </div>
                    </div>
                    <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {compositionCatalog.differentiation.status}
                    </span>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-4">
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Profiles</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {compositionCatalog.differentiation.distinctProfileCount}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Variants</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {compositionCatalog.differentiation.distinctDesignVariantCount}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Unique brick coverage</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {compositionCatalog.differentiation.uniqueBrickCoverageCount}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Avg recommended bricks</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {compositionCatalog.differentiation.averageRecommendedBrickCount}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {compositionCatalog.differentiation.families.slice(0, 6).map((family) => (
                      <div key={family.profileId} className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="text-sm font-semibold text-foreground">{family.title}</div>
                          <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {family.designVariantId}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">{family.profileId}</div>
                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                          <span className="rounded-full border border-border px-2 py-1">{family.recommendedBrickCount} recommended</span>
                          <span className="rounded-full border border-border px-2 py-1">{family.premiumBrickCount} premium</span>
                          <span className="rounded-full border border-border px-2 py-1">{family.sourceRepoCount} repos</span>
                          <span className="rounded-full border border-border px-2 py-1">{family.attributeCount} traits</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Archetype catalog</div>
                  <div className="mt-3 space-y-3">
                    {compositionCatalog.archetypes.map((archetype) => {
                      const theme = getLongwallsDesignTheme(archetype.id);
                      const differentiationFamily = compositionCatalog.differentiation.families.find(
                        (family) => family.archetypeId === archetype.id,
                      );
                      const previewStyle = {
                        background: `${theme.workspaceTexture}, ${theme.canvas}`,
                        color: theme.ink,
                        fontFamily: theme.typeface,
                        boxShadow: theme.shadow,
                      } satisfies CSSProperties;

                      const accentStyle = {
                        background: theme.accent,
                        color: theme.accentInk,
                        borderColor: theme.line,
                      } satisfies CSSProperties;

                      return (
                        <div key={archetype.id} className="overflow-hidden rounded-[28px] border border-border bg-muted/10">
                          <div
                            className="border-b border-border px-4 py-4"
                            style={{ background: `${theme.heroTexture}, ${theme.panelAlt}` }}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="space-y-2">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
                                  {archetype.experienceLabel}
                                </div>
                                <div className="text-base font-semibold text-foreground">{archetype.title}</div>
                                <div className="max-w-2xl text-sm text-muted-foreground">{archetype.heroNarrative}</div>
                              </div>
                              <span
                                className="rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide"
                                style={accentStyle}
                              >
                                {archetype.moduleBadge}
                              </span>
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="rounded-[24px] border border-white/40 px-4 py-4" style={previewStyle}>
                              <div className="flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.18em]">
                                <span className="rounded-full border px-2 py-1">{archetype.workspaceMode}</span>
                                <span className="rounded-full border px-2 py-1">{archetype.density}</span>
                                <span className="rounded-full border px-2 py-1">{archetype.motionProfile}</span>
                              </div>
                              <div className="mt-4 grid gap-3 lg:grid-cols-[1.15fr,0.85fr]">
                                <div className="space-y-3">
                                  <div className="text-sm font-semibold">{archetype.visualThesis}</div>
                                  <div className="flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.18em] opacity-80">
                                    <span className="rounded-full border px-2 py-1">{archetype.layoutFamily}</span>
                                    <span className="rounded-full border px-2 py-1">{archetype.workflowFamily}</span>
                                  </div>
                                  <p className="text-sm opacity-80">{archetype.summary}</p>
                                </div>
                                <div className="space-y-2">
                                  {archetype.proofScenarios.slice(0, 2).map((scenario) => (
                                    <div key={scenario} className="rounded-2xl border border-current/10 bg-white/50 px-3 py-3 text-xs opacity-90">
                                      {scenario}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                              <span className="rounded-full border border-border px-2 py-1">
                                {archetype.securityDefaults.length} security defaults
                              </span>
                              <span className="rounded-full border border-border px-2 py-1">
                                {archetype.recommendedBrickIds.length} recommended bricks
                              </span>
                              {differentiationFamily ? (
                                <>
                                  <span className="rounded-full border border-border px-2 py-1">
                                    {differentiationFamily.profileId}
                                  </span>
                                  <span className="rounded-full border border-border px-2 py-1">
                                    {differentiationFamily.designVariantId}
                                  </span>
                                </>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Brick catalog</div>
                  <div className="mt-3 space-y-3">
                    {compositionCatalog.bricks.map((brick) => (
                      <div key={brick.id} className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-semibold text-foreground">{brick.title}</div>
                          <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {brick.family}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground">{brick.summary}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid gap-3 xl:grid-cols-[1.2fr,1fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Harvested sources</div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        Phase 0 locks the real repo scope behind the next brick wave instead of pretending the catalog starts from zero.
                      </div>
                    </div>
                    <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Phase 0
                    </span>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    {compositionCatalog.harvest.sourceRepos.map((repo) => (
                      <div key={repo.id} className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="text-sm font-semibold text-foreground">{repo.title}</div>
                          <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {repo.tier}
                          </span>
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">{repo.role}</p>
                        <div className="mt-3 flex items-center justify-between gap-3 text-[11px] uppercase tracking-wide text-muted-foreground">
                          <span>{repo.candidateCount} candidates</span>
                          <span>{repo.worktreePosture}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Priority packs</div>
                    <div className="mt-3 space-y-3">
                      {compositionCatalog.harvest.priorityPacks.map((pack) => (
                        <div key={pack.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="text-sm font-semibold text-foreground">{pack.title}</div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {pack.targetPhase}
                            </span>
                          </div>
                          <p className="mt-2 text-xs text-muted-foreground">{pack.summary}</p>
                          <div className="mt-3 text-[11px] uppercase tracking-wide text-muted-foreground">
                            {pack.candidateCount} candidates / {pack.sourceRepoIds.length} source repos / {pack.status}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Highlight candidates</div>
                    <div className="mt-3 space-y-3">
                      {compositionCatalog.harvest.candidateHighlights.map((candidate) => (
                        <div key={candidate.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="text-sm font-semibold text-foreground">{candidate.title}</div>
                          <div className="mt-2 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                            <span className="rounded-full border border-border px-2 py-1">{candidate.sourceRepoId}</span>
                            <span className="rounded-full border border-border px-2 py-1">{candidate.maturity}</span>
                            <span className="rounded-full border border-border px-2 py-1">{candidate.reusePotential} reuse</span>
                            <span className="rounded-full border border-border px-2 py-1">{candidate.securityImpact}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {compositionError ?? "Composition catalog loading."}
            </div>
          )}
        </Card>
      </section>

      <section className="home-dashboard__grid" aria-label="LongWalls modules">
        {PRIMARY_MODULES.map((card) => (
          <Card
            key={card.id}
            className={`home-dashboard__card home-dashboard__card--${card.accent}`}
            bodyClassName="home-dashboard__card-body"
          >
            <div className="home-dashboard__card-top">
              <span className="home-dashboard__card-badge">{card.badge}</span>
              <div className="space-y-2">
                <div className="text-lg font-semibold text-foreground">{card.title}</div>
                <p className="text-sm text-muted-foreground">{card.description}</p>
              </div>
            </div>
            <div className="home-dashboard__card-footer">
              <div className="text-xs text-muted-foreground">{card.value}</div>
              <Button variant="secondary" size="sm" onClick={() => navigate(card.href)}>
                Open
              </Button>
            </div>
          </Card>
        ))}
      </section>

      <section aria-label="Enterprise proof board" data-testid="longwalls-proof-board">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Enterprise proof</div>
              <div className="text-lg font-semibold text-foreground">Repeatable A-to-Z delivery posture</div>
              <p className="text-sm text-muted-foreground">
                LongWalls is only sponsor-credible when more than one bounded lot can cross Atlas, Foundry, and Jarvis with the same discipline.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/jarvis")}>
              Open Jarvis
            </Button>
          </div>

          {proofBoard ? (
            <>
              <div className="grid gap-3 md:grid-cols-5">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Reference lots</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {proofBoard.summary.referenceLotsReady}/{proofBoard.summary.targetReferenceLots}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Sponsor-ready pilots</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{proofBoard.summary.sponsorReadyLots}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Average gate score</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{proofBoard.summary.averageGateScore}/100</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Open blockers</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{proofBoard.summary.openBlockers}</div>
                </div>
              </div>
              <div className="rounded-md border border-border bg-background p-3 text-sm text-foreground">
                {proofBoard.summary.headline}
              </div>
              <div className="grid gap-3 xl:grid-cols-[0.9fr,1.1fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Repeatability proof</div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        This board only matters if reuse stays visible while custom delta remains explicit.
                      </div>
                    </div>
                    <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {proofBoard.repeatability.status}
                    </span>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Distinct archetypes</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">{proofBoard.repeatability.distinctArchetypes}</div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Distinct domain families</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">{proofBoard.repeatability.distinctDomainFamilies}</div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Average reuse ratio</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">{proofBoard.repeatability.averageReuseRatio}%</div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Average custom delta load</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">{proofBoard.repeatability.averageCustomDeltaLoad}</div>
                    </div>
                  </div>
                  <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-sm text-foreground">
                    {proofBoard.repeatability.comparisonHeadline}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lot comparison</div>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    {proofBoard.repeatability.lots.map((lot) => (
                      <div key={lot.projectId} className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-foreground">{lot.name}</div>
                            <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                              {lot.code} · {lot.archetypeId ?? "no archetype"}
                            </div>
                          </div>
                          <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {lot.reuseRatio}% reuse
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                          <span className="rounded-full border border-border px-2 py-1">{lot.domainFamily ?? "unknown"}</span>
                          <span className="rounded-full border border-border px-2 py-1">{lot.brickCount} bricks</span>
                          <span className="rounded-full border border-border px-2 py-1">{lot.requiredBrickCount} required</span>
                          <span className="rounded-full border border-border px-2 py-1">{lot.primaryFlowCount} flows</span>
                          <span className="rounded-full border border-border px-2 py-1">{lot.customDeltaLoad} custom delta</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                {proofBoard.lots.map((lot) => (
                  <div key={lot.projectId} className="rounded-md border border-border bg-background p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{lot.code}</div>
                        <div className="mt-1 text-sm font-semibold text-foreground">{lot.name}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          Gate {lot.gateScore}/100 · {lot.greenRuns}/{lot.requiredGreenRuns} green runs · {lot.chainState}
                        </div>
                      </div>
                      <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {lot.gateStatus === "pilot_ready" ? "Sponsor-ready" : "Building"}
                      </span>
                    </div>
                    <div className="mt-3 text-sm text-muted-foreground">{lot.nextMove}</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/atlas?customerId=${encodeURIComponent(lot.customerId)}&projectId=${encodeURIComponent(lot.projectId)}`,
                          )
                        }
                      >
                        Atlas
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => navigate(`/foundry?projectId=${encodeURIComponent(lot.projectId)}`)}>
                        Foundry
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => navigate(`/jarvis?projectId=${encodeURIComponent(lot.projectId)}`)}>
                        Jarvis
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {proofError ?? "Enterprise proof board loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Lighthouse pilot motion" data-testid="longwalls-lighthouse-motion">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Lighthouse pilot motion</div>
              <div className="text-lg font-semibold text-foreground">Commercial motion grounded in runtime truth</div>
              <p className="text-sm text-muted-foreground">
                The sale motion must now stay tied to what LongWalls actually proves: bounded pilots, frozen decisions,
                governed delivery, and explicit evidence.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/longwalls")}>
              Refresh motion
            </Button>
          </div>

          {lighthouseMotion ? (
            <>
              <div className="grid gap-3 md:grid-cols-4">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Readiness</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {lighthouseMotion.readiness.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Executive read</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {lighthouseMotion.summary.executiveReadTimeMinutes} min
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Pilot offer</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{lighthouseMotion.offer.duration}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Positioning</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{lighthouseMotion.summary.positioning}</div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-background p-4">
                <div className="text-sm font-semibold text-foreground">{lighthouseMotion.summary.headline}</div>
                <p className="mt-2 text-sm text-muted-foreground">{lighthouseMotion.summary.executiveNarrative}</p>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.05fr,0.95fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Commercial mapping</div>
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    {Object.entries(lighthouseMotion.commercialMapping).map(([key, role]) => (
                      <div key={key} className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-sm font-semibold text-foreground">{role.title}</div>
                        <div className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">{key}</div>
                        <p className="mt-3 text-sm text-muted-foreground">{role.primaryQuestion}</p>
                        <div className="mt-3 space-y-2">
                          {role.proofPoints.map((point) => (
                            <div key={point} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              {point}
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 text-xs text-muted-foreground">{role.caution}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pilot offer</div>
                  <div className="mt-3 space-y-3">
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-sm font-semibold text-foreground">{lighthouseMotion.offer.name}</div>
                      <div className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">
                        {lighthouseMotion.offer.duration}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Scope</div>
                      <div className="mt-2 space-y-2">
                        {lighthouseMotion.offer.scope.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Exclusions</div>
                      <div className="mt-2 space-y-2">
                        {lighthouseMotion.offer.exclusions.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.05fr,0.95fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Evidence posture</div>
                  <div className="mt-3 grid gap-3 md:grid-cols-3">
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Proven</div>
                      <div className="mt-2 space-y-2">
                        {lighthouseMotion.proof.provenClaims.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Partial</div>
                      <div className="mt-2 space-y-2">
                        {lighthouseMotion.proof.partialClaims.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Generalize next</div>
                      <div className="mt-2 space-y-2">
                        {lighthouseMotion.proof.generalizationFrontier.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Motion artifacts</div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {[
                      ["Pre-read", lighthouseMotion.artifactUrls.preRead],
                      ["Demo script", lighthouseMotion.artifactUrls.demoScript],
                      ["Pilot offer", lighthouseMotion.artifactUrls.pilotOffer],
                      ["Commercial role guide", lighthouseMotion.artifactUrls.roleGuideCommercial],
                      ["Evidence pack", lighthouseMotion.artifactUrls.evidencePack],
                      ["Motion JSON", lighthouseMotion.artifactUrls.motionPackJson],
                    ].map(([label, href]) => (
                      <a
                        key={label}
                        href={href}
                        className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {lighthouseMotionError ?? "Lighthouse pilot motion loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Live-account conversion" data-testid="longwalls-live-account-conversion">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Live-account conversion</div>
              <div className="text-lg font-semibold text-foreground">Eclosia bounded live-pilot protocol</div>
              <p className="text-sm text-muted-foreground">
                The benchmark remains explicit, but the next move is now a bounded live-account protocol with
                readiness domains, phase gates, rollback, and scorecard discipline.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/longwalls")}>
              Refresh conversion pack
            </Button>
          </div>

          {liveAccountConversion ? (
            <>
              <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-9">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Account</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{liveAccountConversion.account.name}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Benchmark app</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {liveAccountConversion.account.benchmarkApplication}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Go / no-go</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {liveAccountConversion.goNoGo.status.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Rerun score</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {liveAccountConversion.comparison.rerun.weightedTotals.engineRerun}/
                    {liveAccountConversion.comparison.rerun.weightedTotals.goThreshold}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Absorbed lots</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {liveAccountConversion.comparison.rerun.absorbedLots.filter((lot) => lot.status === "absorbed").length}/
                    {liveAccountConversion.comparison.rerun.absorbedLots.length}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Passes closed</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {liveAccountConversion.summary.passesClosed}/{liveAccountConversion.summary.totalPasses}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Readiness</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {liveAccountConversion.summary.boundedPilotReadinessPercent}%
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Live launch</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {liveAccountConversion.summary.liveLaunchStatus.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Pilot slice</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{liveAccountConversion.pilot.sliceName}</div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-background p-4">
                <div className="text-sm font-semibold text-foreground">{liveAccountConversion.summary.headline}</div>
                <p className="mt-2 text-sm text-muted-foreground">{liveAccountConversion.summary.narrative}</p>
                <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                  {liveAccountConversion.summary.motionDependency}
                </div>
                <div className="mt-3 rounded-md border border-border bg-background p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Pilot next gate</div>
                  <div className="mt-2 text-sm text-muted-foreground">{liveAccountConversion.pilotProtocol.nextGate}</div>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.05fr,0.95fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Three-pass protocol</div>
                  <div className="mt-3 grid gap-3">
                    {liveAccountConversion.passes.map((pass) => (
                      <div key={pass.id} className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-foreground">{pass.title}</div>
                            <div className="mt-1 text-xs text-muted-foreground">{pass.goal}</div>
                          </div>
                          <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {pass.status.replace(/_/g, " ")}
                          </span>
                        </div>
                        <div className="mt-3 grid gap-3 lg:grid-cols-2">
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Deliverables</div>
                            <div className="mt-2 space-y-2">
                              {pass.deliverables.map((item) => (
                                <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Blockers</div>
                            <div className="mt-2 space-y-2">
                              {pass.blockers.map((item) => (
                                <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Account readiness</div>
                        <div className="mt-1 text-sm font-semibold text-foreground">
                          {liveAccountConversion.accountReadiness.overallStatus.replace(/_/g, " ")} ·{" "}
                          {liveAccountConversion.accountReadiness.readinessPercent}%
                        </div>
                      </div>
                      <div className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {liveAccountConversion.summary.liveLaunchStatus.replace(/_/g, " ")}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {liveAccountConversion.accountReadiness.domains.map((domain) => (
                        <div key={domain.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="text-sm font-semibold text-foreground">{domain.title}</div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {domain.status.replace(/_/g, " ")}
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">{domain.why}</div>
                          <div className="mt-3 space-y-1">
                            {domain.evidence.map((item) => (
                              <div key={item} className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                                {item}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Comparison frame</div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                      {liveAccountConversion.comparison.decisionRule}
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Manual existing</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {liveAccountConversion.comparison.rerun.weightedTotals.manualExisting}/100
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Engine only</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {liveAccountConversion.comparison.rerun.weightedTotals.engineBlind}/100
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Reuse baseline</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {liveAccountConversion.comparison.rerun.weightedTotals.engineReuseBaseline}/100
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Rerun benchmark</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {liveAccountConversion.comparison.rerun.weightedTotals.engineRerun}/
                          {liveAccountConversion.comparison.rerun.weightedTotals.goThreshold}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-background p-3">
                      <div className="text-sm font-semibold text-foreground">
                        {liveAccountConversion.comparison.rerun.verdict === "go"
                          ? "GO for bounded pilot"
                          : "NO-GO for bounded pilot"}
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">
                        Gap to manual existing: {liveAccountConversion.comparison.rerun.weightedTotals.gapToManualExisting} points
                      </div>
                      <div className="mt-3 space-y-2">
                        {liveAccountConversion.comparison.rerun.rationale.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {liveAccountConversion.comparison.dimensions.map((dimension) => (
                        <div key={dimension.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="text-sm font-semibold text-foreground">{dimension.title}</div>
                          <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                            {dimension.weight}% weight
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">{dimension.description}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Absorbed lots</div>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        {liveAccountConversion.comparison.rerun.absorbedLots.map((lot) => (
                          <div key={lot.lotId} className="rounded-md border border-border bg-background p-2">
                            <div className="text-xs font-semibold text-foreground">{lot.title}</div>
                            <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                              {lot.status.replace(/_/g, " ")}
                            </div>
                            <div className="mt-2 space-y-1">
                              {lot.impact.map((item) => (
                                <div key={item} className="rounded-md border border-border bg-muted/10 p-1.5 text-[11px] text-muted-foreground">
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pilot protocol</div>
                        <div className="mt-1 text-sm font-semibold text-foreground">
                          {liveAccountConversion.pilotProtocol.status.replace(/_/g, " ")}
                        </div>
                      </div>
                      <div className="text-right text-[11px] uppercase tracking-wide text-muted-foreground">
                        <div>{liveAccountConversion.pilotProtocol.launchMode.replace(/_/g, " ")}</div>
                        <div>{liveAccountConversion.pilotProtocol.deploymentPreference.replace(/_/g, " ")}</div>
                      </div>
                    </div>
                    <div className="mt-3 space-y-3">
                      {liveAccountConversion.pilotProtocol.phases.map((phase) => (
                        <div key={phase.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <div className="text-sm font-semibold text-foreground">{phase.title}</div>
                              <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                                Owner: {phase.owner}
                              </div>
                            </div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {phase.status.replace(/_/g, " ")}
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">{phase.objective}</div>
                          <div className="mt-3 grid gap-3 lg:grid-cols-3">
                            <div>
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Entry criteria</div>
                              <div className="mt-2 space-y-2">
                                {phase.entryCriteria.map((item) => (
                                  <div key={item} className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                                    {item}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Exit criteria</div>
                              <div className="mt-2 space-y-2">
                                {phase.exitCriteria.map((item) => (
                                  <div key={item} className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                                    {item}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Proof</div>
                              <div className="mt-2 space-y-2">
                                {phase.proof.map((item) => (
                                  <div key={item} className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                                    {item}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pilot shape</div>
                    <div className="mt-3 grid gap-3">
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-sm font-semibold text-foreground">{liveAccountConversion.pilot.sliceName}</div>
                        <div className="mt-2 text-xs text-muted-foreground">{liveAccountConversion.pilot.businessIntent}</div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">In scope</div>
                          <div className="mt-2 space-y-2">
                            {liveAccountConversion.pilot.inScope.map((item) => (
                              <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                                {item}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Out of scope</div>
                          <div className="mt-2 space-y-2">
                            {liveAccountConversion.pilot.outOfScope.map((item) => (
                              <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                                {item}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Roles</div>
                        <div className="mt-2 grid gap-2 sm:grid-cols-3">
                          <div className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            Sponsor: {liveAccountConversion.account.sponsorRole}
                          </div>
                          <div className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            Buyer: {liveAccountConversion.account.buyerRole}
                          </div>
                          <div className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            Operator: {liveAccountConversion.account.operatorRole}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[0.95fr,1.05fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Go / no-go</div>
                  <div className="mt-3 rounded-md border border-border bg-muted/10 p-3">
                    <div className="text-sm font-semibold text-foreground">
                      {liveAccountConversion.goNoGo.status === "go" ? "GO for bounded pilot" : "NO-GO for bounded pilot"}
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">{liveAccountConversion.goNoGo.rationale}</div>
                  </div>
                  <div className="mt-3 grid gap-3 lg:grid-cols-2">
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Required to reach GO</div>
                      <div className="mt-2 space-y-2">
                        {liveAccountConversion.goNoGo.requiredToReachGo.length ? (
                          liveAccountConversion.goNoGo.requiredToReachGo.map((item) => (
                            <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              {item}
                            </div>
                          ))
                        ) : (
                          <div className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            No remaining product blocker inside the bounded dispatch-core scope.
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">GO threshold</div>
                      <div className="mt-2 space-y-2">
                        {liveAccountConversion.goNoGo.threshold.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Conversion artifacts</div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {[
                      ["Blind brief", liveAccountConversion.artifactUrls.blindBrief],
                      ["Blind scorecard", liveAccountConversion.artifactUrls.blindScorecard],
                      ["Reuse delta", liveAccountConversion.artifactUrls.reuseDelta],
                      ["Engine vs manual scorecard", liveAccountConversion.artifactUrls.comparisonScorecard],
                      ["Pilot scope", liveAccountConversion.artifactUrls.pilotScope],
                      ["Risk register", liveAccountConversion.artifactUrls.riskRegister],
                      ["Go / no-go", liveAccountConversion.artifactUrls.goNoGo],
                      ["Bounded pilot protocol", liveAccountConversion.artifactUrls.pilotProtocol],
                      ["Execution checklist", liveAccountConversion.artifactUrls.executionChecklist],
                      ["Rollback plan", liveAccountConversion.artifactUrls.rollbackPlan],
                      ["Success scorecard", liveAccountConversion.artifactUrls.successScorecard],
                      ["Conversion JSON", liveAccountConversion.artifactUrls.conversionPackJson],
                    ].map(([label, href]) => (
                      <a
                        key={label}
                        href={href}
                        className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[0.95fr,1.05fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rollback lane</div>
                  <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                    {liveAccountConversion.rollback.strategy}
                  </div>
                  <div className="mt-3 grid gap-3 lg:grid-cols-2">
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Triggers</div>
                      <div className="mt-2 space-y-2">
                        {liveAccountConversion.rollback.triggers.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Actions</div>
                      <div className="mt-2 space-y-2">
                        {liveAccountConversion.rollback.actions.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Success scorecard</div>
                  <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                    {liveAccountConversion.successScorecard.northStar}
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {liveAccountConversion.successScorecard.metrics.map((metric) => (
                      <div key={metric.id} className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="text-sm font-semibold text-foreground">{metric.title}</div>
                          <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {metric.status.replace(/_/g, " ")}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">{metric.target}</div>
                        <div className="mt-3 grid gap-2 lg:grid-cols-2">
                          <div className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                            Source: {metric.source}
                          </div>
                          <div className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                            Owner: {metric.owner}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {liveAccountConversionError ?? "Live-account conversion pack loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Live pilot proof" data-testid="longwalls-live-pilot-proof">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Live pilot proof</div>
              <div className="text-lg font-semibold text-foreground">Bounded live lane, without fake production claims</div>
              <p className="text-sm text-muted-foreground">
                Lot 35 closes the gap between motion and runtime proof: checklist execution, support runbook, rollback drill,
                and attestation are now exposed as a bounded live lane while customer runtime verification stays explicit.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/longwalls")}>
              Refresh live pilot proof
            </Button>
          </div>

          {livePilotProof ? (
            <>
              <div className="grid gap-3 md:grid-cols-6">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {livePilotProof.status.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Launch mode</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {livePilotProof.summary.launchMode.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Readiness</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {livePilotProof.summary.boundedPilotReadinessPercent}%
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Checks</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {livePilotProof.runtimeProof.summary.passCount} pass / {livePilotProof.runtimeProof.summary.watchCount} watch
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Failures</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {livePilotProof.runtimeProof.summary.failCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Production runtime</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {livePilotProof.summary.productionRuntimeVerified ? "verified" : "not verified"}
                  </div>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.05fr,0.95fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-sm font-semibold text-foreground">{livePilotProof.summary.headline}</div>
                  <p className="mt-2 text-sm text-muted-foreground">{livePilotProof.summary.narrative}</p>
                  <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                    {livePilotProof.boundedPilot.sliceName} Â· {livePilotProof.boundedPilot.accountName} Â·{" "}
                    {livePilotProof.boundedPilot.benchmarkApplication}
                  </div>
                  <div className="mt-3 rounded-md border border-border bg-background p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Next gate</div>
                    <div className="mt-2 text-sm text-muted-foreground">{livePilotProof.summary.nextGate}</div>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Support lane</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">{livePilotProof.supportRunbook.ownerLane}</div>
                      <div className="mt-2 space-y-2">
                        {livePilotProof.supportRunbook.executedChecks.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Rollback drill</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {livePilotProof.rollbackDrill.status.replace(/_/g, " ")}
                      </div>
                      <div className="mt-2 text-xs text-muted-foreground">{livePilotProof.rollbackDrill.restoredControlPlane}</div>
                      <div className="mt-3 space-y-2">
                        {livePilotProof.rollbackDrill.steps.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Runtime proof</div>
                        <div className="mt-1 text-sm font-semibold text-foreground">
                          {livePilotProof.runtimeProof.summary.status.replace(/_/g, " ")}
                        </div>
                      </div>
                      <div className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {livePilotProof.runtimeProof.runtimeLane.liveMutationStatus.replace(/_/g, " ")}
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {livePilotProof.runtimeProof.healthChecks.map((check) => (
                        <div key={check.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="text-sm font-semibold text-foreground">{check.label}</div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {check.status}
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">{check.summary}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Attestation and limits</div>
                    <div className="mt-3 grid gap-3">
                      <div className="rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                        {livePilotProof.releaseAttestation.status.replace(/_/g, " ")}
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Assertions</div>
                        <div className="mt-2 space-y-2">
                          {livePilotProof.releaseAttestation.assertions.map((item) => (
                            <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Limitations</div>
                        <div className="mt-2 space-y-2">
                          {livePilotProof.boundedPilot.limitations.map((item) => (
                            <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Live pilot artifacts</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {[
                        ["Live pilot JSON", livePilotProof.artifactUrls.packJson],
                        ["Runtime proof", livePilotProof.artifactUrls.runtimeProof],
                        ["Support runbook", livePilotProof.artifactUrls.supportRunbook],
                        ["Rollback drill", livePilotProof.artifactUrls.rollbackDrill],
                        ["Release attestation", livePilotProof.artifactUrls.releaseAttestation],
                        ["Execution checklist", livePilotProof.artifactUrls.executionChecklist],
                        ["Rollback plan", livePilotProof.artifactUrls.rollbackPlan],
                        ["Success scorecard", livePilotProof.artifactUrls.successScorecard],
                      ].map(([label, href]) => (
                        <a
                          key={label}
                          href={href}
                          className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {livePilotProofError ?? "Live pilot proof pack loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Customer runtime bridge" data-testid="longwalls-customer-runtime-bridge">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Customer runtime bridge</div>
              <div className="text-lg font-semibold text-foreground">GO only when runtime proof, rollback, and budget controls all hold</div>
              <p className="text-sm text-muted-foreground">
                This bridge closes the gap between the bounded live proof and a runtime-real customer lane. It stays explicit:
                customer runtime can be verified while production runtime remains unverified.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/longwalls")}>
              Open runtime control
            </Button>
          </div>

          {customerRuntimeBridge ? (
            <>
              <div className="grid gap-3 md:grid-cols-6">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Bridge status</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {customerRuntimeBridge.status.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Lot 38 gate</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{customerRuntimeBridge.summary.lot38GoNoGo}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Customer runtime</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {customerRuntimeBridge.summary.customerRuntimeVerified ? "verified" : "not verified"}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Production runtime</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">not verified</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Runtime owner</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {customerRuntimeBridge.liveAccountScopeFreeze.scopeFreeze.runtimeOwner.accountScope}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Run ID</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {customerRuntimeBridge.summary.runId ?? "unresolved"}
                  </div>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.05fr,0.95fr]">
                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-sm font-semibold text-foreground">{customerRuntimeBridge.summary.headline}</div>
                    <p className="mt-2 text-sm text-muted-foreground">{customerRuntimeBridge.summary.narrative}</p>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                      {customerRuntimeBridge.summary.nextMove}
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Scope freeze</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {customerRuntimeBridge.liveAccountScopeFreeze.scopeFreeze.sliceName}
                        </div>
                        <div className="mt-3 space-y-2">
                          {customerRuntimeBridge.liveAccountScopeFreeze.checks.map((check) => (
                            <div key={check.id} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              {check.label}: {check.status} - {check.summary}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Budget guardrails</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {customerRuntimeBridge.budgetGuardrails.status}
                        </div>
                        <div className="mt-3 space-y-2">
                          {customerRuntimeBridge.budgetGuardrails.checks.map((check) => (
                            <div key={check.id} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              {check.label}: {check.status}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Shadow run lane</div>
                    <div className="mt-2 text-sm font-semibold text-foreground">
                      {customerRuntimeBridge.shadowRun.status} · {customerRuntimeBridge.shadowRun.laneType.replace(/_/g, " ")}
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">{customerRuntimeBridge.shadowRun.note}</div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                      Preview: {customerRuntimeBridge.shadowRun.previewUrl ?? "unresolved"}
                    </div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-3">
                      {customerRuntimeBridge.shadowRun.endpointChecks.map((check) => (
                        <div key={check.id} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                          {check.id}: {check.status} {check.httpStatus ? `(${check.httpStatus})` : ""}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rollback and support</div>
                    <div className="mt-3 grid gap-3">
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-sm font-semibold text-foreground">
                          Rollback: {customerRuntimeBridge.rollbackDrill.status}
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">{customerRuntimeBridge.rollbackDrill.note}</div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-sm font-semibold text-foreground">
                          Support drill: {customerRuntimeBridge.supportDrill.status}
                        </div>
                        <div className="mt-2 space-y-2">
                          {customerRuntimeBridge.supportDrill.executedChecks.map((item) => (
                            <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Attestation</div>
                    <div className="mt-2 text-sm font-semibold text-foreground">{customerRuntimeBridge.attestation.status.replace(/_/g, " ")}</div>
                    <div className="mt-3 space-y-2">
                      {customerRuntimeBridge.attestation.assertions.map((item) => (
                        <div key={item} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                          {item}
                        </div>
                      ))}
                      {customerRuntimeBridge.attestation.limitations.map((item) => (
                        <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Bridge artifacts</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {[
                        ["Bridge JSON", customerRuntimeBridge.artifactUrls.packJson],
                        ["Budget guardrails", customerRuntimeBridge.artifactUrls.budgetGuardrails],
                        ["Shadow run", customerRuntimeBridge.artifactUrls.shadowRun],
                        ["Rollback drill", customerRuntimeBridge.artifactUrls.rollbackDrill],
                        ["Support drill", customerRuntimeBridge.artifactUrls.supportDrill],
                        ["Attestation", customerRuntimeBridge.artifactUrls.attestation],
                      ].map(([label, href]) => (
                        <a
                          key={label}
                          href={href}
                          className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {customerRuntimeBridgeError ?? "Customer runtime bridge pack loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Production runtime verification" data-testid="longwalls-production-runtime-verification">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Production runtime verification</div>
              <div className="text-lg font-semibold text-foreground">
                Control-plane production proof without pretending generated-app production is done
              </div>
              <p className="text-sm text-muted-foreground">
                This packet verifies the LongWalls control-plane production lane only: Vercel production, protected API,
                rollback readiness, support runbook, and egress-safe runtime controls.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/jarvis")}>
              Open Jarvis control
            </Button>
          </div>

          {productionRuntimeVerification ? (
            <>
              <div className="grid gap-3 md:grid-cols-5">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {productionRuntimeVerification.status.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Scope</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {productionRuntimeVerification.summary.productionRuntimeScope.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Pass / fail</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {productionRuntimeVerification.summary.passCount} / {productionRuntimeVerification.summary.failCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Production URL</div>
                  <div className="mt-2 truncate text-sm font-semibold text-foreground">
                    {productionRuntimeVerification.summary.productionTargetUrl ?? "unresolved"}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Lot 42</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {productionRuntimeVerification.releaseState.lot42Unblocked ? "unblocked" : "blocked"}
                  </div>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1fr,1fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-sm font-semibold text-foreground">
                    {productionRuntimeVerification.summary.headline}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {productionRuntimeVerification.summary.narrative}
                  </p>
                  <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                    {productionRuntimeVerification.summary.nextMove}
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {[
                      productionRuntimeVerification.accountFreeze,
                      productionRuntimeVerification.topology,
                      productionRuntimeVerification.envIam,
                      productionRuntimeVerification.backupRestore,
                      productionRuntimeVerification.dataSafety,
                      productionRuntimeVerification.liveWindow,
                      productionRuntimeVerification.rollbackDrill,
                    ].map((record) => (
                      <div key={record.schema} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                        {record.schema.replace("longwalls.production-runtime-", "").replace("@1", "")}: {record.status}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Limitations and evidence</div>
                  <div className="mt-3 space-y-2">
                    {productionRuntimeVerification.limitations.map((item) => (
                      <div key={item} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {[
                      ["Pack JSON", productionRuntimeVerification.artifactUrls.packJson],
                      ["Vercel topology", productionRuntimeVerification.artifactUrls.topology],
                      ["Env IAM", productionRuntimeVerification.artifactUrls.envIam],
                      ["Rollback", productionRuntimeVerification.artifactUrls.rollbackDrill],
                      ["Runbook", productionRuntimeVerification.artifactUrls.supportRunbook],
                      ["Attestation", productionRuntimeVerification.artifactUrls.attestation],
                    ].map(([label, href]) => (
                      <a
                        key={label}
                        href={href}
                        className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {productionRuntimeVerificationError ?? "Production runtime verification pack loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Legacy reverse-engineering intake" data-testid="longwalls-legacy-reverse-engineering">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Legacy intake</div>
              <div className="text-lg font-semibold text-foreground">Repo-grounded legacy intelligence</div>
              <p className="text-sm text-muted-foreground">
                Lot 29 turns legacy repos into a deterministic Foundry opening: recovered modules, explicit gaps,
                reset blueprint, and promotion bundle instead of blank-sheet reinterpretation.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/foundry")}>
              Open Foundry
            </Button>
          </div>

          {legacyReverseEngineering ? (
            <>
              <div className="grid gap-3 md:grid-cols-7">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {legacyReverseEngineering.status.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Repos</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {legacyReverseEngineering.summary.groundedRepoCount}/{legacyReverseEngineering.summary.repoCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">API specs</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {legacyReverseEngineering.summary.apiSpecCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Foundry sections</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {legacyReverseEngineering.summary.proposedSectionCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Modules</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {legacyReverseEngineering.summary.moduleCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Blocking gaps</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {legacyReverseEngineering.summary.blockingGapCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Promotion</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {legacyReverseEngineering.summary.promotionReadiness.replace(/_/g, " ")}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-background p-4">
                <div className="text-sm font-semibold text-foreground">{legacyReverseEngineering.summary.headline}</div>
                <p className="mt-2 text-sm text-muted-foreground">{legacyReverseEngineering.summary.narrative}</p>
                <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                  {legacyReverseEngineering.summary.nextMove}
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.05fr,0.95fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Grounded repo dossiers</div>
                  <div className="mt-3 grid gap-3">
                    {legacyReverseEngineering.repos.map((repo) => (
                      <div key={repo.id} className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-foreground">{repo.title}</div>
                            <div className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                              {repo.domainHypothesis.replace(/_/g, " ")}
                            </div>
                          </div>
                          <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {repo.status.replace(/_/g, " ")}
                          </span>
                        </div>
                        <div className="mt-2 text-xs text-muted-foreground">{repo.sponsorNarrative}</div>
                        <div className="mt-3 grid gap-3 lg:grid-cols-2">
                          <div className="space-y-2">
                            <div className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                              Package: {repo.packageName ?? "n/a"}
                            </div>
                            <div className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                              Manager: {repo.packageManager ?? "n/a"}
                            </div>
                            <div className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                              Foundry lift: {repo.foundryLift.confidenceScore}
                            </div>
                            <div className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                              Risk: {repo.riskLevel}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                              Docs: {repo.observedAssets.docsCount} · API specs: {repo.observedAssets.apiSpecCount} · UI surfaces: {repo.observedAssets.uiSurfaceCount}
                            </div>
                            <div className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                              Tests: {repo.observedAssets.hasTests ? "yes" : "no"} · Prisma: {repo.observedAssets.hasPrisma ? "yes" : "no"} · Supabase: {repo.observedAssets.hasSupabase ? "yes" : "no"}
                            </div>
                            <div className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                              First extraction: {repo.reversePlan.firstExtraction}
                            </div>
                            <div className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                              Recovered modules: {repo.extractedModules.length} · Repo gaps: {repo.repoGapIds.length}
                            </div>
                          </div>
                        </div>
                        {repo.extractedModules.length > 0 ? (
                          <div className="mt-3 space-y-2">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Recovered modules</div>
                            <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                              {repo.extractedModules.slice(0, 4).map((module) => (
                                <span
                                  key={module.id}
                                  className="rounded-full border border-border bg-background px-2 py-1"
                                  title={module.rationale}
                                >
                                  {module.title} · {module.reusePotential}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                          {repo.frameworkSignals.map((item) => (
                            <span key={`${repo.id}-framework-${item}`} className="rounded-full border border-border px-2 py-1">
                              {item}
                            </span>
                          ))}
                          {repo.integrationSignals.map((item) => (
                            <span key={`${repo.id}-integration-${item}`} className="rounded-full border border-border px-2 py-1">
                              {item}
                            </span>
                          ))}
                          {repo.runtimeSignals.map((item) => (
                            <span key={`${repo.id}-runtime-${item}`} className="rounded-full border border-border px-2 py-1">
                              {item}
                            </span>
                          ))}
                        </div>
                        {repo.foundryLift.blocker ? (
                          <div className="mt-3 rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                            Blocker: {repo.foundryLift.blocker}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Foundry promotion bundle</div>
                    <div className="mt-3 grid gap-3">
                      {legacyReverseEngineering.foundryPromotionBundle.sectionBundles.map((section) => (
                        <div key={section.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="text-sm font-semibold text-foreground">{section.title}</div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {section.autoFillReadiness}
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">{section.rationale}</div>
                          <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                            {section.sourceRepoIds.map((repoId) => (
                              <span key={`${section.id}-${repoId}`} className="rounded-full border border-border px-2 py-1">
                                {repoId}
                              </span>
                            ))}
                          </div>
                          {section.moduleIds.length > 0 ? (
                            <div className="mt-3 rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                              Modules: {section.moduleIds.length}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Gap analysis</div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                      {legacyReverseEngineering.gapAnalysis.headline}
                    </div>
                    <div className="mt-3 grid gap-3 lg:grid-cols-2">
                      <div className="rounded-md border border-border bg-background p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Blocking</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {legacyReverseEngineering.gapAnalysis.blockingGapCount}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-background p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Watch</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {legacyReverseEngineering.gapAnalysis.watchGapCount}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {legacyReverseEngineering.gapAnalysis.crossRepoGaps.map((gap) => (
                        <div key={gap.id} className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                          <div className="font-semibold text-foreground">{gap.title}</div>
                          <div className="mt-1">{gap.recommendation}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reverse-engineering protocol</div>
                    <div className="mt-3 grid gap-3">
                      {legacyReverseEngineering.extractionProtocol.phases.map((phase) => (
                        <div key={phase.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="text-sm font-semibold text-foreground">{phase.title}</div>
                          <div className="mt-2 text-xs text-muted-foreground">{phase.objective}</div>
                          <div className="mt-3 grid gap-3 lg:grid-cols-2">
                            <div>
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Deliverables</div>
                              <div className="mt-2 space-y-2">
                                {phase.deliverables.map((item) => (
                                  <div key={item} className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                                    {item}
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Exit criteria</div>
                              <div className="mt-2 space-y-2">
                                {phase.exitCriteria.map((item) => (
                                  <div key={item} className="rounded-md border border-border bg-background p-2 text-[11px] text-muted-foreground">
                                    {item}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reset blueprint</div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                      {legacyReverseEngineering.resetBlueprint.headline}
                    </div>
                    <div className="mt-3 space-y-3">
                      {legacyReverseEngineering.resetBlueprint.phases.map((phase) => (
                        <div key={phase.id} className="rounded-md border border-border bg-background p-3">
                          <div className="text-sm font-semibold text-foreground">{phase.title}</div>
                          <div className="mt-2 text-xs text-muted-foreground">{phase.objective}</div>
                          <div className="mt-2 text-[11px] text-muted-foreground">{phase.riskReduction}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.05fr,0.95fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Why this matters</div>
                  <div className="mt-3 space-y-2">
                    {legacyReverseEngineering.benchmarkImpact.whyThisMatters.map((item) => (
                      <div key={item} className="rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
                    {legacyReverseEngineering.benchmarkImpact.longwallsLeverage}
                  </div>
                </div>

                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Legacy intake artifacts</div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {[
                      ["Legacy intake JSON", legacyReverseEngineering.artifactUrls.legacyReverseEngineeringJson],
                      ["Repo dossier", legacyReverseEngineering.artifactUrls.repoDossier],
                      ["Repo matrix", legacyReverseEngineering.artifactUrls.repoMatrix],
                      ["Foundry handoff", legacyReverseEngineering.artifactUrls.foundryHandoff],
                      ["Reset blueprint", legacyReverseEngineering.artifactUrls.resetBlueprint],
                    ].map(([label, href]) => (
                      <a
                        key={label}
                        href={href}
                        className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {label}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {legacyReverseEngineeringError ?? "Legacy reverse-engineering pack loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Atlas generalization" data-testid="longwalls-atlas-generalization">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Atlas generalization</div>
              <div className="text-lg font-semibold text-foreground">Heterogeneous sponsor-grade intake pack</div>
              <p className="text-sm text-muted-foreground">
                Lot 19 pushes Atlas past the proof set with a divergent dossier pack v2, a scoring robustness scorecard,
                and an Atlas to Foundry export contract v3.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/atlas")}>
              Open Atlas
            </Button>
          </div>

          {atlasGeneralization ? (
            <>
              <div className="grid gap-3 md:grid-cols-6">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {atlasGeneralization.status.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Dossiers</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {atlasGeneralization.summary.dossierCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Domain families</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {atlasGeneralization.summary.domainFamilyCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Source repos</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {atlasGeneralization.summary.sourceRepoCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Stress cases</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {atlasGeneralization.summary.stressCaseCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Min defensibility
                  </div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {atlasGeneralization.summary.minimumDefensibilityScore}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-background p-4">
                <div className="text-sm font-semibold text-foreground">{atlasGeneralization.summary.headline}</div>
                <p className="mt-2 text-sm text-muted-foreground">{atlasGeneralization.summary.narrative}</p>
                <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                  {atlasGeneralization.summary.nextMove}
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.05fr,0.95fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Heterogeneous dossier pack v2
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Proof replay base
                      </div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {atlasGeneralization.diversityPack.proofReplayCount}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Divergent extensions
                      </div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        {atlasGeneralization.diversityPack.divergentExtensionCount}
                      </div>
                    </div>
                    <div className="rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Version</div>
                      <div className="mt-2 text-sm font-semibold text-foreground">
                        v{atlasGeneralization.diversityPack.version}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-3">
                    {atlasGeneralization.diversityPack.dossiers.map((dossier) => (
                      <div key={dossier.id} className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-foreground">{dossier.title}</div>
                            <div className="mt-1 text-xs text-muted-foreground">{dossier.operatingModel}</div>
                          </div>
                          <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {dossier.domainFamily.replace(/_/g, " ")}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                          <span className="rounded-full border border-border px-2 py-1">{dossier.sourceRepoId}</span>
                          <span className="rounded-full border border-border px-2 py-1">
                            {dossier.recommendedArchetypeId}
                          </span>
                          <span className="rounded-full border border-border px-2 py-1">
                            {dossier.groundingMode.replace(/_/g, " ")}
                          </span>
                        </div>
                        <div className="mt-3 grid gap-2 lg:grid-cols-[1.1fr,0.9fr]">
                          <div className="rounded-md border border-border bg-background p-3">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Signals</div>
                            <div className="mt-2 space-y-2">
                              {dossier.keySignals.map((signal) => (
                                <div key={signal} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                                  {signal}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="rounded-md border border-border bg-background p-3">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Divergence axes
                            </div>
                            <div className="mt-2 space-y-2">
                              {dossier.divergenceAxes.map((axis) => (
                                <div key={axis} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                                  {axis}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 grid gap-2 lg:grid-cols-[1.05fr,0.95fr]">
                          <div className="rounded-md border border-border bg-background p-3">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Stress cases
                            </div>
                            <div className="mt-2 space-y-2">
                              {dossier.stressCases.map((item) => (
                                <div key={item} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="rounded-md border border-border bg-background p-3">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Scoring factors
                            </div>
                            <div className="mt-2 space-y-2">
                              {dossier.scoringFactors.map((item) => (
                                <div key={item} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                                  {item}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 rounded-md border border-border bg-background p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Repo path</div>
                          <div className="mt-2 text-xs text-muted-foreground">{dossier.repoPath}</div>
                        </div>
                        <div className="mt-3 rounded-md border border-border bg-background p-3">
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Sponsor questions
                          </div>
                          <div className="mt-2 space-y-2">
                            {dossier.sponsorQuestions.map((question) => (
                              <div key={question} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                                {question}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Scoring robustness scorecard
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Robustness status
                        </div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {atlasGeneralization.scoringRobustness.status.replace(/_/g, " ")}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Non-cousin dossiers
                        </div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {atlasGeneralization.scoringRobustness.nonCousinDossierCount}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Avg defensibility
                        </div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {atlasGeneralization.scoringRobustness.averageDefensibilityScore}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Avg transferability
                        </div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {atlasGeneralization.scoringRobustness.averageTransferabilityScore}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                      {atlasGeneralization.scoringRobustness.headline}
                    </div>
                    <div className="mt-3 space-y-2">
                      {atlasGeneralization.scoringRobustness.dossierScores.map((score) => (
                        <div key={score.dossierId} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <div className="text-sm font-semibold text-foreground">{score.title}</div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                {score.domainFamily.replace(/_/g, " ")}
                              </div>
                            </div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {score.confidence}
                            </span>
                          </div>
                          <div className="mt-3 grid gap-2 sm:grid-cols-3">
                            <div className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              Defensibility {score.defensibilityScore}
                            </div>
                            <div className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              Transferability {score.transferabilityScore}
                            </div>
                            <div className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              Export readiness {score.exportReadinessScore}
                            </div>
                          </div>
                          <div className="mt-3 space-y-2">
                            {score.rationale.map((item) => (
                              <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                                {item}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Executive output pack v2</div>
                    <div className="mt-3 space-y-2">
                      {atlasGeneralization.outputPackV2.sponsorOutputs.map((item) => (
                        <div key={item} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                          {item}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                      {atlasGeneralization.outputPackV2.founderReading}
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                      {atlasGeneralization.outputPackV2.executiveAssertion}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Atlas export contract v3</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Required artifacts</div>
                        <div className="mt-2 space-y-2">
                          {atlasGeneralization.exportContractV3.requiredArtifacts.map((item) => (
                            <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Required decision fields</div>
                        <div className="mt-2 space-y-2">
                          {atlasGeneralization.exportContractV3.requiredDecisionFields.map((item) => (
                            <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Blocking conditions
                      </div>
                      <div className="mt-2 space-y-2">
                        {atlasGeneralization.exportContractV3.blockingConditions.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Recommendation contract
                      </div>
                      <div className="mt-2 space-y-2">
                        {atlasGeneralization.exportContractV3.recommendationContract.requiredQuestions.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Why this matters</div>
                      <div className="mt-2 space-y-2">
                        {atlasGeneralization.benchmarkImpact.whyThisMatters.map((item) => (
                          <div key={item} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Atlas generalization artifacts</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {[
                        ["Generalization JSON", atlasGeneralization.artifactUrls.atlasGeneralizationJson],
                        ["Diversity pack v2", atlasGeneralization.artifactUrls.diversityPackV2],
                        ["Scoring robustness", atlasGeneralization.artifactUrls.scoringRobustness],
                        ["Executive pack JSON", atlasGeneralization.artifactUrls.executivePortfolioPackJson],
                        ["Executive pack MD", atlasGeneralization.artifactUrls.executivePortfolioPackMarkdown],
                        ["Export contract v3", atlasGeneralization.artifactUrls.exportContractV3],
                      ].map(([label, href]) => (
                        <a
                          key={label}
                          href={href}
                          className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {atlasGeneralizationError ?? "Atlas generalization pack loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Foundry generalization" data-testid="longwalls-foundry-generalization">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Foundry generalization</div>
              <div className="text-lg font-semibold text-foreground">Hardened build-ready contract for heterogeneous projects</div>
              <p className="text-sm text-muted-foreground">
                Lot 20 makes Foundry less optimistic: completeness validation, external ingestion scoring, and contract v3 now separate custom delta, production blockers, and runtime constraints.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/foundry")}>
              Open Foundry
            </Button>
          </div>

          {foundryGeneralization ? (
            <>
              <div className="grid gap-3 md:grid-cols-4">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {foundryGeneralization.status.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Ingestion lanes</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {foundryGeneralization.summary.ingestionLaneCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Completeness sections</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {foundryGeneralization.summary.completenessSectionCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">External scenarios</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {foundryGeneralization.summary.externalScenarioCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Blocking conditions</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {foundryGeneralization.summary.blockingConditionCount}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-background p-4">
                <div className="text-sm font-semibold text-foreground">{foundryGeneralization.summary.headline}</div>
                <p className="mt-2 text-sm text-muted-foreground">{foundryGeneralization.summary.narrative}</p>
                <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                  {foundryGeneralization.summary.nextMove}
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.02fr,0.98fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">External ingestion lanes</div>
                  <div className="mt-3 grid gap-3">
                    {foundryGeneralization.externalIngestion.lanes.map((lane) => (
                      <div key={lane.id} className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-foreground">{lane.title}</div>
                            <div className="mt-1 text-xs text-muted-foreground">{lane.summary}</div>
                          </div>
                          <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            {lane.githubMode.replace(/-/g, " ")}
                          </span>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                          <span className="rounded-full border border-border px-2 py-1">{lane.projectType.replace(/-/g, " ")}</span>
                        </div>
                        <div className="mt-3 space-y-2">
                          {lane.requiredSignals.map((signal) => (
                            <div key={signal} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                              {signal}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Topology and compliance</div>
                    <div className="mt-3 space-y-3">
                      {foundryGeneralization.topologyAndCompliance.scenarios.map((scenario) => (
                        <div key={scenario.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="text-sm font-semibold text-foreground">{scenario.title}</div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {scenario.complianceProfile.replace(/-/g, " ")}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                            <span className="rounded-full border border-border px-2 py-1">{scenario.topology.replace(/-/g, " ")}</span>
                            <span className="rounded-full border border-border px-2 py-1">{scenario.auth}</span>
                            <span className="rounded-full border border-border px-2 py-1">{scenario.observability}</span>
                          </div>
                          <div className="mt-3 space-y-2">
                            {scenario.requiredControls.map((control) => (
                              <div key={control} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                                {control}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Build-ready rigor</div>
                    <div className="mt-3 grid gap-3 md:grid-cols-3">
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Contract v3</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {foundryGeneralization.buildReadyRigor.contractStatus.replace(/-/g, " ")}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Contract score</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">{foundryGeneralization.buildReadyRigor.contractScore}</div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Runtime constraints</div>
                        <div className="mt-2 text-sm font-semibold text-foreground">
                          {foundryGeneralization.buildReadyRigor.runtimeConstraintCount}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 space-y-3">
                      {foundryGeneralization.buildReadyRigor.blockerFamilies.map((blocker) => (
                        <div key={blocker.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="text-sm font-semibold text-foreground">{blocker.title}</div>
                            <div className="flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-muted-foreground">
                              <span className="rounded-full border border-border px-2 py-1">{blocker.source}</span>
                              <span className="rounded-full border border-border px-2 py-1">{blocker.severity}</span>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">{blocker.trigger}</div>
                          <div className="mt-2 rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {blocker.requiredResolution}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[0.95fr,1.05fr]">
                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Why this matters</div>
                  <div className="mt-3 space-y-2">
                    {foundryGeneralization.benchmarkImpact.whyThisMatters.map((item) => (
                      <div key={item} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                    {foundryGeneralization.benchmarkImpact.lot11Dependency}
                  </div>
                </div>

                <div className="rounded-md border border-border bg-background p-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Foundry generalization artifacts</div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {[
                      ["Generalization JSON", foundryGeneralization.artifactUrls.foundryGeneralizationJson],
                      ["Readiness matrix", foundryGeneralization.artifactUrls.readinessMatrix],
                      ["Foundry contract v3", foundryGeneralization.artifactUrls.foundryContractV3],
                      ["Completeness validator", foundryGeneralization.artifactUrls.completenessValidator],
                      ["External ingestion scorecard", foundryGeneralization.artifactUrls.externalIngestionScorecard],
                    ].map(([label, href]) => (
                      <a
                        key={label}
                        href={href}
                        className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {label}
                      </a>
                    ))}
                  </div>
                  <div className="mt-4 rounded-md border border-border bg-muted/10 p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Benchmark signals</div>
                    <div className="mt-2 space-y-2">
                      {foundryGeneralization.buildReadyRigor.benchmarkSignals.map((signal) => (
                        <div key={signal} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                          {signal}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {foundryGeneralizationError ?? "Foundry generalization pack loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Jarvis depth" data-testid="longwalls-jarvis-depth">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Jarvis depth</div>
              <div className="text-lg font-semibold text-foreground">Structural assembly depth and generated-app divergence</div>
              <p className="text-sm text-muted-foreground">
                Lot 14 makes depth visible: custom delta, structural signatures, and generated-app divergence are now carried through runtime truth instead of staying trapped in a generic assembly summary.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/jarvis")}>
              Open Jarvis
            </Button>
          </div>

          {jarvisDepth ? (
            <>
              <div className="grid gap-3 md:grid-cols-4">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{jarvisDepth.status.replace(/_/g, " ")}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Reference lots</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{jarvisDepth.summary.referenceLots}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Avg depth score</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{jarvisDepth.summary.averageDepthScore}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Avg divergence score</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{jarvisDepth.summary.averageDivergenceScore}</div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-background p-4">
                <div className="text-sm font-semibold text-foreground">{jarvisDepth.summary.headline}</div>
                <p className="mt-2 text-sm text-muted-foreground">{jarvisDepth.summary.narrative}</p>
                <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                  {jarvisDepth.summary.nextMove}
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[0.95fr,1.05fr]">
                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Depth scorecard</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                        <div className="text-[11px] font-semibold uppercase tracking-wide">Validated generated apps</div>
                        <div className="mt-2 text-base font-semibold text-foreground">{jarvisDepth.depthScorecard.generatedAppsValidated}</div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                        <div className="text-[11px] font-semibold uppercase tracking-wide">Structural signatures</div>
                        <div className="mt-2 text-base font-semibold text-foreground">{jarvisDepth.depthScorecard.distinctStructuralSignatures}</div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                        <div className="text-[11px] font-semibold uppercase tracking-wide">Minimum depth</div>
                        <div className="mt-2 text-base font-semibold text-foreground">{jarvisDepth.depthScorecard.minimumDepthScore}</div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                        <div className="text-[11px] font-semibold uppercase tracking-wide">Minimum divergence</div>
                        <div className="mt-2 text-base font-semibold text-foreground">{jarvisDepth.depthScorecard.minimumDivergenceScore}</div>
                      </div>
                    </div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                      Average reuse ratio: {jarvisDepth.depthScorecard.averageReuseRatio}%
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Custom delta trace</div>
                    <p className="mt-2 text-sm text-muted-foreground">{jarvisDepth.customDeltaTrace.headline}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {jarvisDepth.customDeltaTrace.categoriesCovered.map((category) => (
                        <span
                          key={category}
                          className="rounded-full border border-border bg-muted/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                    <div className="mt-4 space-y-2">
                      {jarvisDepth.customDeltaTrace.lots.map((lot) => (
                        <div key={lot.projectId} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="text-sm font-semibold text-foreground">{lot.name}</div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              load {lot.customDeltaLoad}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {lot.distinguishingAttributes.map((attribute) => (
                              <span
                                key={`${lot.projectId}-${attribute}`}
                                className="rounded-full border border-border bg-background px-2 py-1 text-[11px] font-semibold text-foreground"
                              >
                                {attribute}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Generated-app divergence</div>
                    <p className="mt-2 text-sm text-muted-foreground">{jarvisDepth.generatedAppDivergence.headline}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {jarvisDepth.generatedAppDivergence.structuralSignatures.map((signature) => (
                        <span
                          key={signature}
                          className="rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700"
                        >
                          {signature}
                        </span>
                      ))}
                    </div>
                    <div className="mt-4 space-y-3">
                      {jarvisDepth.generatedAppDivergence.lots.map((lot) => (
                        <div key={lot.projectId} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <div className="text-sm font-semibold text-foreground">{lot.name}</div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                {lot.archetypeId ?? "no archetype"} · {lot.domainFamily ?? "domain n/a"}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              <span className="rounded-full border border-border px-2 py-1">depth {lot.depthScore}</span>
                              <span className="rounded-full border border-border px-2 py-1">divergence {lot.divergenceScore}</span>
                              <span className="rounded-full border border-border px-2 py-1">reuse {lot.reuseRatio}%</span>
                            </div>
                          </div>
                          <div className="mt-2 rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {lot.structuralSignature}
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {lot.distinguishingAttributes.map((attribute) => (
                              <span
                                key={`${lot.projectId}-signal-${attribute}`}
                                className="rounded-full border border-border bg-background px-2 py-1 text-[11px] font-semibold text-foreground"
                              >
                                {attribute}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Jarvis depth artifacts</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {[
                        ["Depth JSON", jarvisDepth.artifactUrls.jarvisDepthJson],
                        ["Depth scorecard", jarvisDepth.artifactUrls.scorecard],
                        ["Custom delta trace", jarvisDepth.artifactUrls.customDeltaTrace],
                        ["Divergence matrix", jarvisDepth.artifactUrls.divergenceMatrix],
                      ].map(([label, href]) => (
                        <a
                          key={label}
                          href={href}
                          className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {jarvisDepthError ?? "Jarvis depth pack loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Conciergerie deep assembly" data-testid="longwalls-conciergerie-assembly">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Jarvis Conciergerie assembly</div>
              <div className="text-lg font-semibold text-foreground">Role-native dispatch core generated from Foundry</div>
              <p className="text-sm text-muted-foreground">
                Lot 41 proves that Jarvis can consume the Conciergerie external-intake dossier and assemble a dispatch core with separate requester, dispatcher, runner, and admin surfaces instead of a generic dashboard shell.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/jarvis")}>
              Review in Jarvis
            </Button>
          </div>

          {conciergerieAssembly ? (
            <>
              <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-7">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {conciergerieAssembly.status.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Roles</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {conciergerieAssembly.summary.roleSurfaceCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Flows</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {conciergerieAssembly.summary.primaryFlowCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Data entities</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {conciergerieAssembly.summary.dataEntityCount}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Custom delta</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{conciergerieAssembly.summary.customDeltaLoad}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Depth</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{conciergerieAssembly.summary.depthScore}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Divergence</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {conciergerieAssembly.summary.divergenceScore}
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-background p-4">
                <div className="text-sm font-semibold text-foreground">{conciergerieAssembly.summary.headline}</div>
                <p className="mt-2 text-sm text-muted-foreground">{conciergerieAssembly.summary.narrative}</p>
                <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                  {conciergerieAssembly.summary.nextMove}
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.1fr,0.9fr]">
                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Role-native surfaces</div>
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      {conciergerieAssembly.roleSurfaces.map((surface) => (
                        <div key={surface.role} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <div className="text-sm font-semibold text-foreground">{surface.title}</div>
                              <div className="mt-1 text-xs text-muted-foreground">{surface.surface}</div>
                            </div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {surface.role}
                            </span>
                          </div>
                          <div className="mt-3 rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            {surface.primaryQuestion}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {surface.primaryFlows.map((flow) => (
                              <span
                                key={`${surface.role}-${flow}`}
                                className="rounded-full border border-border bg-background px-2 py-1 text-[11px] font-semibold text-foreground"
                              >
                                {flow}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Flow graph</div>
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      {conciergerieAssembly.flowGraph.slice(0, 10).map((flow) => (
                        <div key={flow.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="text-sm font-semibold text-foreground">{flow.title}</div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {flow.ownerRole}
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-muted-foreground">{flow.exitEvidence}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assembly contract</div>
                    <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <span className="font-semibold text-foreground">Archetype:</span>{" "}
                        {conciergerieAssembly.assemblyContract.archetypeId}
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <span className="font-semibold text-foreground">Profile:</span>{" "}
                        {conciergerieAssembly.assemblyContract.differentiationProfileId}
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3">
                        <span className="font-semibold text-foreground">Design:</span>{" "}
                        {conciergerieAssembly.assemblyContract.designVariantId}
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3 text-xs">
                        {conciergerieAssembly.assemblyContract.structuralSignature}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Custom delta</div>
                    <p className="mt-2 text-sm text-muted-foreground">{conciergerieAssembly.customDelta.summary}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {[
                        ...conciergerieAssembly.customDelta.domainRules,
                        ...conciergerieAssembly.customDelta.customUiRequirements,
                        ...conciergerieAssembly.customDelta.customIntegrations,
                      ]
                        .slice(0, 8)
                        .map((item) => (
                          <span
                            key={item}
                            className="rounded-full border border-border bg-muted/10 px-2 py-1 text-[11px] font-semibold text-foreground"
                          >
                            {item}
                          </span>
                        ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Target comparison</div>
                    <div className="mt-2 text-sm font-semibold text-foreground">
                      {conciergerieAssembly.targetComparison.verdict.replace(/_/g, " ")}
                    </div>
                    <div className="mt-3 space-y-2">
                      {conciergerieAssembly.targetComparison.nonGenericSignals.map((signal) => (
                        <div key={signal} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                          {signal}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Conciergerie artifacts</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {[
                        ["Assembly JSON", conciergerieAssembly.artifactUrls.assemblyJson],
                        ["Role surface map", conciergerieAssembly.artifactUrls.roleSurfaceMap],
                        ["Custom delta report", conciergerieAssembly.artifactUrls.customDeltaReport],
                        ["Target comparison", conciergerieAssembly.artifactUrls.targetComparison],
                        ["Generated app manifest", conciergerieAssembly.artifactUrls.generatedAppManifest],
                      ].map(([label, href]) => (
                        <a
                          key={label}
                          href={href}
                          className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {conciergerieAssemblyError ?? "Conciergerie assembly pack loading."}
            </div>
          )}
        </Card>
      </section>

      <section aria-label="Jarvis default usage" data-testid="longwalls-jarvis-default-usage">
        <Card className="home-dashboard__card" bodyClassName="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="home-dashboard__card-badge">Jarvis default usage</div>
              <div className="text-lg font-semibold text-foreground">Jarvis-first control tower, routines, and pilot governance</div>
              <p className="text-sm text-muted-foreground">
                Lot 21 turns Jarvis into the default review and pilotage surface. Founder, tactical, and operator routines now carry explicit rooms, actions, control loops, and bounded exit rules.
              </p>
            </div>
            <Button variant="secondary" size="sm" onClick={() => navigate("/jarvis")}>
              Open Jarvis room
            </Button>
          </div>

          {jarvisDefaultUsage ? (
            <>
              <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {jarvisDefaultUsage.status.replace(/_/g, " ")}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Default surface</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">{jarvisDefaultUsage.summary.defaultSurface}</div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Room coverage</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {jarvisDefaultUsage.summary.roomCoverageScore}/100
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Command coverage</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {jarvisDefaultUsage.summary.commandCoverageScore}/100
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Control tower score</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {jarvisDefaultUsage.summary.controlTowerScore}/100
                  </div>
                </div>
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Latency proxy</div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {jarvisDefaultUsage.summary.decisionLatencyReductionPercent}% faster
                  </div>
                </div>
              </div>

              <div className="rounded-md border border-border bg-background p-4">
                <div className="text-sm font-semibold text-foreground">{jarvisDefaultUsage.summary.headline}</div>
                <p className="mt-2 text-sm text-muted-foreground">{jarvisDefaultUsage.summary.narrative}</p>
                <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-xs text-muted-foreground">
                  {jarvisDefaultUsage.summary.nextMove}
                </div>
              </div>

              <div className="grid gap-3 xl:grid-cols-[1.05fr,0.95fr]">
                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Role routines</div>
                    <div className="mt-3 space-y-3">
                      {jarvisDefaultUsage.roleRoutines.map((routine) => (
                        <div key={routine.role} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <div className="text-sm font-semibold text-foreground">
                                {routine.role === "founder"
                                  ? "Founder default review"
                                  : routine.role === "tactical"
                                    ? "Tactical default review"
                                    : "Operator default review"}
                              </div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                {routine.room}
                                {routine.roomUrl ? ` · ${routine.roomUrl}` : ""}
                              </div>
                            </div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {routine.readiness}
                            </span>
                          </div>
                          <div className="mt-3 rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                            {routine.defaultQuestion}
                          </div>
                          <div className="mt-3 grid gap-3 md:grid-cols-2">
                            <div className="space-y-2">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Required signals</div>
                              {routine.requiredSignals.map((signal) => (
                                <div key={`${routine.role}-${signal}`} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                                  {signal}
                                </div>
                              ))}
                            </div>
                            <div className="space-y-2">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Primary actions</div>
                              {routine.primaryActions.map((action) => (
                                <div key={`${routine.role}-${action}`} className="rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                                  {action}
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="mt-3 rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                            Cadence: {routine.cadence}
                            <div className="mt-1">{routine.fallbackIfMissing}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Decision latency proxy</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                        <div className="text-[11px] font-semibold uppercase tracking-wide">Surface switches</div>
                        <div className="mt-2 text-base font-semibold text-foreground">
                          {jarvisDefaultUsage.decisionLatency.currentSurfaceCount}/{jarvisDefaultUsage.decisionLatency.baselineSurfaceCount}
                        </div>
                      </div>
                      <div className="rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                        <div className="text-[11px] font-semibold uppercase tracking-wide">Review minutes</div>
                        <div className="mt-2 text-base font-semibold text-foreground">
                          {jarvisDefaultUsage.decisionLatency.currentReviewMinutes}/{jarvisDefaultUsage.decisionLatency.baselineReviewMinutes}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {jarvisDefaultUsage.decisionLatency.rationale.map((item) => (
                        <div key={item} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Review matrix</div>
                    <div className="mt-3 space-y-2">
                      {jarvisDefaultUsage.reviewRoutines.matrix.map((row) => (
                        <div key={`${row.role}-${row.trigger}`} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="text-sm font-semibold text-foreground">{row.role}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{row.trigger}</div>
                          <div className="mt-2 text-xs text-muted-foreground">
                            {row.firstSurface} → {row.secondSurface} → {row.decisionOutput}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Control tower loops</div>
                    <div className="mt-3 space-y-2">
                      {jarvisDefaultUsage.controlTower.loops.map((loop) => (
                        <div key={loop.id} className="rounded-md border border-border bg-muted/10 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="text-sm font-semibold text-foreground">{loop.title}</div>
                            <span className="rounded-full border border-border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {loop.readiness}
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">{loop.trigger}</div>
                          <div className="mt-2 text-xs text-muted-foreground">
                            {loop.firstSurface} {"->"} {loop.supportingSurface}
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {loop.actions.map((action) => (
                              <span
                                key={`${loop.id}-${action}`}
                                className="rounded-full border border-border bg-background px-2 py-1 text-[11px] font-semibold text-foreground"
                              >
                                {action}
                              </span>
                            ))}
                          </div>
                          <div className="mt-3 rounded-md border border-border bg-background p-2 text-xs text-muted-foreground">
                            Proof: {loop.proofObjects.join(", ")}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pilot governance</div>
                    <div className="mt-3 rounded-md border border-border bg-muted/10 p-3 text-sm text-muted-foreground">
                      <div className="font-semibold text-foreground">{jarvisDefaultUsage.pilotGovernance.headline}</div>
                      <div className="mt-2">
                        Status: {jarvisDefaultUsage.pilotGovernance.status} | Start surface:{" "}
                        {jarvisDefaultUsage.pilotGovernance.reviewStartSurface}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {jarvisDefaultUsage.pilotGovernance.commandPath.map((step) => (
                        <span
                          key={step}
                          className="rounded-full border border-border bg-muted/10 px-2 py-1 text-[11px] font-semibold text-foreground"
                        >
                          {step}
                        </span>
                      ))}
                    </div>
                    <div className="mt-3 space-y-2">
                      {jarvisDefaultUsage.pilotGovernance.requiredChecks.map((item) => (
                        <div key={item} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                          Check: {item}
                        </div>
                      ))}
                      {jarvisDefaultUsage.pilotGovernance.freezeConditions.map((item) => (
                        <div key={item} className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-700">
                          Freeze: {item}
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 space-y-2">
                      {jarvisDefaultUsage.surfacePolicy.requiredProofBeforeExit.map((item) => (
                        <div key={item} className="rounded-md border border-border bg-muted/10 p-2 text-xs text-muted-foreground">
                          Proof before exit: {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-background p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Jarvis default usage artifacts</div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {[
                        ["Default usage JSON", jarvisDefaultUsage.artifactUrls.defaultUsageJson],
                        ["Default usage playbook", jarvisDefaultUsage.artifactUrls.playbook],
                        ["Review routines matrix", jarvisDefaultUsage.artifactUrls.routinesMatrix],
                        ["Decision latency scorecard", jarvisDefaultUsage.artifactUrls.latencyScorecard],
                        ["Control tower scorecard", jarvisDefaultUsage.artifactUrls.controlTower],
                        ["Pilot governance map", jarvisDefaultUsage.artifactUrls.pilotGovernance],
                      ].map(([label, href]) => (
                        <a
                          key={label}
                          href={href}
                          className="rounded-md border border-border bg-muted/10 p-3 text-sm font-medium text-foreground transition hover:border-primary/40 hover:bg-muted/20"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {label}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
              {jarvisDefaultUsageError ?? "Jarvis default usage pack loading."}
            </div>
          )}
        </Card>
      </section>

      <section className="longwalls-shell__line" aria-label="LongWalls operating line">
        {OPERATING_LINE.map((step) => (
          <Card key={step.id} className="longwalls-shell__line-card" bodyClassName="space-y-3">
            <div className="text-sm font-semibold text-slate-900">{step.title}</div>
            <p className="text-sm leading-6 text-slate-600">{step.description}</p>
          </Card>
        ))}
      </section>

      <section className="longwalls-shell__secondary" aria-label="Supporting surfaces">
        {SECONDARY_SURFACES.map((surface) => (
          <button
            key={surface.id}
            type="button"
            className="longwalls-shell__secondary-card"
            onClick={() => navigate(surface.href)}
          >
            <span className="longwalls-shell__secondary-title">{surface.title}</span>
            <span className="longwalls-shell__secondary-copy">{surface.description}</span>
          </button>
        ))}
      </section>
    </div>
  );
}
